/**
 * Pure fairness-analysis helpers (#247, NFR-6.7). Used off the hot path — by the server's
 * telemetry, the (Slice-B) `render` swimlane, and future adjudication phases. None of
 * these ever decide a buzz winner; they only *measure* what a normalized timeline would
 * have done (FR-4.2 is unchanged).
 */

import type { BuzzOrderAttempt, SyncState } from './types';

/**
 * Map a client monotonic instant to its server-equivalent time via a {@link SyncState}:
 * `serverMs = clientMs + offsetMs`.
 */
export function toServerTime(clientMonoMs: number, syncState: SyncState): number {
  return clientMonoMs + syncState.offsetMs;
}

/**
 * Inverse of {@link toServerTime}: map a **server** monotonic instant back to the caller's
 * local monotonic clock via a {@link SyncState}: `clientMs = serverMs − offsetMs` (offset is
 * server − client, {@link SyncState.offsetMs}). A client uses this to turn a future-dated
 * server-time `OPEN_AT` (#264, FR-4.13) into a local monotonic deadline it can schedule its
 * buzzer unlock against. Pure; monotonic both ends; ports to a native client unchanged (#104).
 */
export function toClientTime(serverMonoMs: number, syncState: SyncState): number {
  return serverMonoMs - syncState.offsetMs;
}

/**
 * Would the buzz winner change if the race were decided by **normalized press time**
 * instead of **server-arrival order**? Telemetry only (the "would-flip" headline signal).
 *
 * The arrival winner is the attempt with the smallest `arrivalMs`. The normalized winner
 * is the attempt with the smallest `normalizedPressMs`. Returns `false` (no flip) when
 * there are fewer than two attempts, or when any attempt lacks a normalized press time
 * (an incomplete picture must never be reported as a flip). Ties are broken by preserving
 * arrival order, so a normalized tie never reports a flip.
 */
export function wouldFlip(attempts: BuzzOrderAttempt[]): boolean {
  if (attempts.length < 2) return false;
  if (attempts.some((a) => a.normalizedPressMs === undefined)) return false;

  const arrivalWinner = pickWinner(attempts, (a) => a.arrivalMs);
  const normalizedWinner = pickWinner(
    attempts,
    (a) => a.normalizedPressMs as number,
    (a) => a.arrivalMs,
  );
  return arrivalWinner.id !== normalizedWinner.id;
}

/**
 * The issue's headline delivery metric: for a candidate open-lookahead of `lookaheadMs`,
 * what fraction of players would have received the `OPEN` in time? Given each player's
 * observed open-delivery delay (server-open → client-receive, ms), a player is "in time"
 * when their delay ≤ the lookahead. Returns 0 for an empty set.
 */
export function deliveredBeforeFraction(
  deliveryDelaysMs: number[],
  lookaheadMs: number,
): number {
  if (deliveryDelaysMs.length === 0) return 0;
  const inTime = deliveryDelaysMs.filter((d) => d <= lookaheadMs).length;
  return inTime / deliveryDelaysMs.length;
}

/**
 * Winner = the attempt minimizing `key`, breaking ties by `tieKey` (default: arrival
 * order via the array index, i.e. the earliest-seen attempt wins a tie). Deterministic.
 */
function pickWinner(
  attempts: BuzzOrderAttempt[],
  key: (a: BuzzOrderAttempt) => number,
  tieKey?: (a: BuzzOrderAttempt) => number,
): BuzzOrderAttempt {
  let winner = attempts[0]!;
  for (let i = 1; i < attempts.length; i += 1) {
    const cand = attempts[i]!;
    const dk = key(cand) - key(winner);
    if (dk < 0) {
      winner = cand;
    } else if (dk === 0 && tieKey && tieKey(cand) < tieKey(winner)) {
      winner = cand;
    }
  }
  return winner;
}
